import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

const industryKeys = [
  "form.industry_0",
  "form.industry_1",
  "form.industry_2",
  "form.industry_3",
  "form.industry_4",
  "form.industry_5",
  "form.industry_6",
];

const budgetKeys = [
  "form.budget_0",
  "form.budget_1",
  "form.budget_2",
  "form.budget_3",
];

const goalKeys = [
  "form.goal_0",
  "form.goal_1",
  "form.goal_2",
  "form.goal_3",
  "form.goal_4",
];

export default function LeadForm() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState<string>("");
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);

  const toggleGoal = (g: string) => {
    setSelectedGoals((prev) =>
      prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]
    );
  };

  const openDemo = () => {
    window.open("https://calendly.com/alex-alcalde-aidealmedia-pyr_/30min", "_blank", "noopener,noreferrer");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Honeypot
    const honeypot = String(formData.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setSelectedGoals([]);
      return;
    }

    const message = String(formData.get("message") || "");
    if (message.length > 500) {
      setStatus("error");
      setFormError(t("form.error_char_limit"));
      return;
    }

    setStatus("loading");
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: "4dbce1cc-e748-484b-861b-c1bd72b4a3e7",
          subject: `Nuevo lead web: ${String(formData.get("company") || "")} - ${String(formData.get("name") || "")}`,
          from_name: String(formData.get("name") || "Web AIdeal Media"),
          name: String(formData.get("name") || ""),
          email: String(formData.get("email") || ""),
          company: String(formData.get("company") || ""),
          website: String(formData.get("website") || ""),
          industry: String(formData.get("industry") || ""),
          budget: String(formData.get("budget") || ""),
          goals: selectedGoals.join(", "),
          message,
        }),
      });
      const result = (await response.json()) as {
        success?: boolean;
        message?: string;
      };

      if (response.ok && result.success) {
        setStatus("success");
        form.reset();
        setSelectedGoals([]);
        return;
      }

      setStatus("error");
      setFormError(result.message || t("form.error_send"));
    } catch {
      setStatus("error");
      setFormError(t("form.error_network"));
    }
  };

  return (
    <section
      id="diagnostico"
      className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-br from-primary-950 via-primary-900 to-primary-950"
    >
      <div className="absolute inset-0 grid-pattern opacity-15 pointer-events-none"></div>
      <div className="absolute top-16 -left-24 w-[420px] h-[420px] radial-glow opacity-40 pointer-events-none"></div>
      <div className="absolute bottom-0 -right-24 w-[520px] h-[520px] radial-glow opacity-30 pointer-events-none"></div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start">
          {/* Left column */}
          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 text-accent-300 text-xs font-semibold border border-background-50/10">
              <span className="w-2 h-2 rounded-full bg-accent-400"></span>
              {t("form.badge")}
            </span>
            <h2 className="mt-5 font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight">
              {t("form.h2_part1")}
              <span className="brand-gradient-text">{t("form.h2_gradient")}</span>
              {t("form.h2_part3")}
            </h2>
            <p className="mt-5 text-base md:text-lg text-background-200/85 leading-relaxed">
              {t("form.description")}
            </p>

            <ul className="mt-8 space-y-3">
              {[0, 1, 2, 3].map((i) => (
                <li key={i} className="flex items-center gap-3 text-background-100/90">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-500/20 text-accent-300 flex-shrink-0">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-check-line text-sm"></i>
                    </span>
                  </span>
                  <span className="text-sm md:text-base">{t(`form.benefit${i}`)}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 p-5 rounded-lg glass-card-dark">
              <p className="text-xs uppercase tracking-widest text-background-200/60">
                {t("form.talk_first")}
              </p>
              <button
                onClick={openDemo}
                className="mt-3 px-6 py-3 rounded-md text-sm font-semibold text-background-50 brand-gradient-btn cursor-pointer whitespace-nowrap inline-flex items-center gap-2 group"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-event-line"></i>
                </span>
                {t("form.talk_cta")}
                <span className="w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-1">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </button>
            </div>
          </div>

          {/* Right column - form */}
          <div className="lg:col-span-3">
            <div className="glass-card-dark rounded-lg p-6 md:p-10">
              {status === "success" ? (
                <div className="py-12 flex flex-col items-center text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-500/20 text-accent-300">
                    <span className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-check-double-line text-3xl"></i>
                    </span>
                  </div>
                  <h3 className="mt-5 font-heading text-2xl md:text-3xl font-bold text-background-50">
                    {t("form.success_title")}
                  </h3>
                  <p className="mt-3 text-background-200/85 max-w-md">
                    {t("form.success_desc")}
                  </p>
                  <button
                    onClick={openDemo}
                    className="mt-6 px-6 py-3 rounded-md text-sm font-semibold text-background-50 brand-gradient-btn cursor-pointer whitespace-nowrap"
                  >
                    {t("form.success_cta")}
                  </button>
                </div>
              ) : (
                <form
                  id="diagnostico-roi-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-background-200/80 mb-1.5">
                        {t("form.label_name")}
                      </label>
                      <input
                        name="name"
                        type="text"
                        required
                        placeholder={t("form.placeholder_name")}
                        className="w-full px-4 py-3 rounded-md bg-background-50/5 border border-background-50/15 text-background-50 placeholder:text-background-300/50 text-sm focus:outline-none focus:border-accent-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-background-200/80 mb-1.5">
                        {t("form.label_email")}
                      </label>
                      <input
                        name="email"
                        type="email"
                        required
                        placeholder={t("form.placeholder_email")}
                        className="w-full px-4 py-3 rounded-md bg-background-50/5 border border-background-50/15 text-background-50 placeholder:text-background-300/50 text-sm focus:outline-none focus:border-accent-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-background-200/80 mb-1.5">
                        {t("form.label_company")}
                      </label>
                      <input
                        name="company"
                        type="text"
                        required
                        placeholder={t("form.placeholder_company")}
                        className="w-full px-4 py-3 rounded-md bg-background-50/5 border border-background-50/15 text-background-50 placeholder:text-background-300/50 text-sm focus:outline-none focus:border-accent-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-background-200/80 mb-1.5">
                        {t("form.label_website")}
                      </label>
                      <input
                        name="website"
                        type="text"
                        placeholder={t("form.placeholder_website")}
                        className="w-full px-4 py-3 rounded-md bg-background-50/5 border border-background-50/15 text-background-50 placeholder:text-background-300/50 text-sm focus:outline-none focus:border-accent-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-background-200/80 mb-1.5">
                        {t("form.label_industry")}
                      </label>
                      <div className="relative">
                        <select
                          name="industry"
                          required
                          defaultValue=""
                          className="w-full appearance-none pr-10 px-4 py-3 rounded-md bg-background-50/5 border border-background-50/15 text-background-50 text-sm focus:outline-none focus:border-accent-400 transition-colors cursor-pointer"
                        >
                          <option value="" disabled className="bg-primary-900">
                            {t("form.placeholder_industry")}
                          </option>
                          {industryKeys.map((k) => (
                            <option key={k} value={t(k)} className="bg-primary-900">
                              {t(k)}
                            </option>
                          ))}
                        </select>
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-background-200/60 pointer-events-none">
                          <i className="ri-arrow-down-s-line"></i>
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-background-200/80 mb-1.5">
                        {t("form.label_budget")}
                      </label>
                      <div className="relative">
                        <select
                          name="budget"
                          required
                          defaultValue=""
                          className="w-full appearance-none pr-10 px-4 py-3 rounded-md bg-background-50/5 border border-background-50/15 text-background-50 text-sm focus:outline-none focus:border-accent-400 transition-colors cursor-pointer"
                        >
                          <option value="" disabled className="bg-primary-900">
                            {t("form.placeholder_budget")}
                          </option>
                          {budgetKeys.map((k) => (
                            <option key={k} value={t(k)} className="bg-primary-900">
                              {t(k)}
                            </option>
                          ))}
                        </select>
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-background-200/60 pointer-events-none">
                          <i className="ri-arrow-down-s-line"></i>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-background-200/80 mb-2">
                      {t("form.label_goals")}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {goalKeys.map((gk) => {
                        const active = selectedGoals.includes(t(gk));
                        return (
                          <button
                            type="button"
                            key={gk}
                            onClick={() => toggleGoal(t(gk))}
                            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors cursor-pointer whitespace-nowrap ${
                              active
                                ? "bg-accent-500 text-primary-950 border-accent-400"
                                : "bg-background-50/5 text-background-100 border-background-50/15 hover:border-accent-400"
                            }`}
                          >
                            {t(gk)}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-background-200/80 mb-1.5">
                      {t("form.label_message")}
                    </label>
                    <textarea
                      name="message"
                      maxLength={500}
                      rows={4}
                      placeholder={t("form.placeholder_message")}
                      className="w-full px-4 py-3 rounded-md bg-background-50/5 border border-background-50/15 text-background-50 placeholder:text-background-300/50 text-sm focus:outline-none focus:border-accent-400 transition-colors resize-none"
                    />
                    <p className="mt-1 text-[11px] text-background-300/60 text-right">
                      {t("form.max_chars")}
                    </p>
                  </div>

                  {/* Honeypot */}
                  <div className="company-alt-field" aria-hidden="true">
                    <label>
                      Company website
                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        readOnly
                      />
                    </label>
                  </div>

                  {status === "error" && formError && (
                    <div className="rounded-md border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                      {formError}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                    <p className="text-[11px] text-background-300/60 max-w-sm">
                      {t("form.privacy_text")}
                    </p>
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="group px-6 py-3 rounded-md text-sm font-semibold text-background-50 brand-gradient-btn cursor-pointer whitespace-nowrap flex items-center gap-2 disabled:opacity-70"
                    >
                      {status === "loading" ? (
                        <>
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-loader-4-line animate-spin"></i>
                          </span>
                          {t("form.submitting")}
                        </>
                      ) : (
                        <>
                          {t("form.submit_label")}
                          <span className="w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-1">
                            <i className="ri-arrow-right-line"></i>
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}